/**
 * Тексти сторінок публічного сайту (адмінка → «Контент» → «Тексти сторінок»).
 *
 * Реєстр TEXT_PAGES — єдине джерело текстів за замовчуванням: сторінка →
 * секція → поле. Компонент бере текст через useT('<секція>'), адмінка будує
 * форму з того самого реєстру; бекенд зберігає лише перекриття
 * (site_content.texts = {"<секція>.<поле>": "текст"}).
 *
 * Розмітка в тексті (rich): *виділення* (колір секції), **жирний**,
 * [текст](/сторінка або https://…), новий рядок = перенос. Змінні:
 * {returns_claim} (блок «Загальне», напр. «45%+»), {returns_num} (лише число з нього, «45»), {fee_pct} (діапазон ставок сітки, «15–35»),
 * {free_months} {threshold} {deposit_fee_pct} {tax_pct} (калькулятор).
 *
 * Тексти, що вже редагуються блоками етапу 1 (контакти, кейси, FAQ-питання,
 * футер, тарифи), тут не дублюються.
 */
import { createElement as h, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { blockOf, calcSettings, claimParts, feeRange, useSiteContent } from './siteContent';
import { PAGES_HOME } from './siteTexts.home';
import { PAGES_OTHER } from './siteTexts.pages';

export const TEXT_PAGES = [...PAGES_HOME, ...PAGES_OTHER];

// "<секція>.<поле>" → текст за замовчуванням
export const TEXT_DEFAULTS = {};
TEXT_PAGES.forEach((p) => p.sections.forEach((s) => s.fields.forEach(([k, , def]) => {
  TEXT_DEFAULTS[`${s.id}.${k}`] = def;
})));

const fmtNum = (v) => Number(v).toLocaleString('uk-UA', { maximumFractionDigits: 2 }).replace(/\u202f|\u00a0/g, ' ');

export function textVars(content) {
  const c = calcSettings(content);
  return {
    returns_claim: blockOf(content, 'general').returns_claim,
    returns_num: claimParts(content) ? fmtNum(claimParts(content).num) : '',
    fee_pct: feeRange(c),
    free_months: fmtNum(c.free_months),
    threshold: fmtNum(c.trading_fee_threshold),
    deposit_fee_pct: fmtNum(c.deposit_fee_pct),
    tax_pct: fmtNum(c.tax_pct),
  };
}

export function fillTextVars(text, vars) {
  return String(text ?? '').replace(/\{([a-z_]+)\}/g, (m, k) => (k in vars ? vars[k] : m));
}

/** Сирий текст (з підставленими змінними). Невідомий ключ — помилка в консолі. */
export function rawText(content, key) {
  const o = content?.texts?.[key];
  if (typeof o === 'string') return o; // порожній рядок — свідомо порожньо (поле сховано)
  if (!(key in TEXT_DEFAULTS)) {
    // eslint-disable-next-line no-console
    console.error(`siteTexts: невідомий ключ тексту «${key}»`);
    return '';
  }
  return TEXT_DEFAULTS[key];
}

const INLINE_RE = /\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

function inline(line, opts, keyBase) {
  const out = [];
  let last = 0;
  let m;
  let n = 0;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(line))) {
    if (m.index > last) out.push(line.slice(last, m.index));
    const key = `${keyBase}-${n++}`;
    if (m[1] !== undefined) {
      out.push(h(opts.boldTag || 'strong', { key }, m[1]));
    } else if (m[2] !== undefined) {
      out.push(opts.accent ? h('span', { key, className: opts.accent }, m[2]) : h(opts.boldTag || 'strong', { key }, m[2]));
    } else {
      const href = m[4];
      if (href.startsWith('/')) out.push(h(Link, { key, to: href, style: opts.linkStyle }, m[3]));
      else if (/^(https?:|mailto:|tel:)/i.test(href)) {
        out.push(h('a', { key, href, target: /^https?:/i.test(href) ? '_blank' : undefined,
          rel: 'noopener noreferrer', style: opts.linkStyle }, m[3]));
      } else out.push(m[0]);
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}

/** Текст з розміткою → вузли React. opts: {accent: className, boldTag, linkStyle}. */
export function renderRich(text, opts = {}) {
  const lines = String(text ?? '').split('\n');
  return lines.flatMap((ln, i) => {
    const parts = inline(ln, opts, `l${i}`);
    return i ? [h('br', { key: `br${i}` }), ...parts] : parts;
  }).map((x, i) => (typeof x === 'string' ? h(Fragment, { key: `s${i}` }, x) : x));
}

/** Текст без розмітки (для alt/placeholder/aria). */
export const plain = (text) => String(text ?? '').replace(INLINE_RE, (m, b, a, lt) => b ?? a ?? lt ?? m);

/**
 * const t = useT('home.hero');
 *   t('title')             → рядок (змінні підставлені, розмітка лишається)
 *   t.rich('lead', opts)   → вузли React з розміткою
 *   t.plain('title')       → рядок без розмітки
 */
export function makeT(content, section) {
  const vars = textVars(content);
  const t = (field) => fillTextVars(rawText(content, `${section}.${field}`), vars);
  t.rich = (field, opts) => renderRich(t(field), opts);
  t.plain = (field) => plain(t(field));
  return t;
}

export function useT(section) {
  return makeT(useSiteContent(), section);
}
